import type { IApiResponse } from '@/helpers/types';

import {
	type IObserverApplicationDetail,
	type IObserverApplicationListParams,
	type IObserverApplicationSummary,
	type IObserverManagementScopeParams,
	OBSERVER_DIVISION_TO_RESULT,
	ObserverApplicationStatus,
	type ObserverDivisionType,
} from './management-types';
import { calculateObserverQualificationScore } from './utils';

interface IMockEntity {
	id: number;
	code: string;
	name: string;
	divisionType: ObserverDivisionType;
}

interface IMockScopeStore {
	entities: IMockEntity[];
	applications: IObserverApplicationDetail[];
}

const stores = new Map<string, IMockScopeStore>();
const subscribers = new Set<() => void>();

const ok = <T>(data: T): IApiResponse<T> => ({
	data,
	isSuccessful: true,
	message: 'Success',
	statusCode: 200,
});

const scopeKey = (scope: IObserverManagementScopeParams) => `${scope.electionObserverId ?? 'token'}:${scope.electionId}`;

const fullName = (application: IObserverApplicationSummary) => `${application.firstName} ${application.lastName}`.trim();

const entitySeeds: Omit<IMockEntity, 'id'>[] = [
	{ code: 'LA', name: 'Lagos', divisionType: 'state' },
	{ code: 'KN', name: 'Kano', divisionType: 'state' },
	{ code: 'LA-01', name: 'Agege', divisionType: 'lga' },
	{ code: 'LA-02', name: 'Ajeromi-Ifelodun', divisionType: 'lga' },
	{ code: 'LA-03', name: 'Alimosho', divisionType: 'lga' },
	{ code: 'LA-01-01', name: 'Isale Oja / Idi Mangoro', divisionType: 'ward' },
	{ code: 'LA-01-02', name: 'Iloro / Onipetesi', divisionType: 'ward' },
	{ code: 'LA-02-01', name: 'Ago Hausa', divisionType: 'ward' },
	{ code: 'LA-01-01-001', name: 'Community Primary School', divisionType: 'pollingUnit' },
	{ code: 'LA-01-01-002', name: 'Town Hall', divisionType: 'pollingUnit' },
	{ code: 'LA-01-02-001', name: 'Local Authority School', divisionType: 'pollingUnit' },
	{ code: 'LA-02-01-001', name: 'Civic Centre', divisionType: 'pollingUnit' },
];

const applicationSeeds = [
	['Ada', 'Okafor', 'LA-01-01-001', 6, 33],
	['Musa', 'Bello', 'LA-01-01-002', 4, 30],
	['Tola', 'Adeyemi', 'LA-01-01-002', 1, 27],
	['Grace', 'Eze', 'LA-01-02-001', 1, 25],
	['Ibrahim', 'Sani', 'LA-01-01', 1, 31],
	['Amina', 'Yusuf', 'LA-01-02', 3, 24],
	['Chidi', 'Nwosu', 'LA-01', 6, 34],
	['Bisi', 'Lawal', 'LA-02', 1, 29],
	['Zainab', 'Aliyu', 'LA', 1, 32],
	['Emeka', 'Obi', 'KN', 5, 22],
] as const;

const entityForCode = (entities: IMockEntity[], code: string) => entities.find((entity) => entity.code === code)!;

const buildStore = (scope: IObserverManagementScopeParams): IMockScopeStore => {
	const entities = entitySeeds.map((entity, index) => ({ ...entity, id: index + 1 }));
	const observerId = scope.electionObserverId ?? 1;
	const applications = applicationSeeds.map(([firstName, lastName, code, statusId, score], index) => {
		const entity = entityForCode(entities, code);
		const appliedAt = new Date(Date.UTC(2026, 5, index + 1, 9)).toISOString();
		const isOffer = statusId === ObserverApplicationStatus.OFFER_SENT;
		const isConfirmed = statusId === ObserverApplicationStatus.CONFIRMED;
		// The seed number drives the five answers; the score is then DERIVED from them, not
		// seeded alongside. Seeded independently the mock served scores of 22-34 against an
		// observer maximum of 25, so the fixture disagreed with its own checklist.
		const qualifications = {
			hasSmartPhoneWithInternet: score >= 20,
			isAvailableAllDay: score >= 25,
			hasPartyAgentExperience: score >= 30,
			isNativeSpeaker: index % 3 !== 0,
			hasPowerBank: score >= 25,
		};
		return {
			id: index + 1,
			applicantId: `mock-applicant-${index + 1}`,
			firstName,
			lastName,
			email: `${firstName}.${lastName}@example.test`.toLowerCase(),
			phoneNumber: `080000000${String(index + 1).padStart(2, '0')}`,
			electionObserverId: observerId,
			electionId: scope.electionId,
			divisionType: entity.divisionType,
			resultType: OBSERVER_DIVISION_TO_RESULT[entity.divisionType],
			entityCode: entity.code,
			entityName: entity.name,
			qualificationScore: calculateObserverQualificationScore(qualifications),
			statusId,
			appliedAt,
			offerSentAt: isOffer ? new Date(Date.UTC(2026, 6, 20, 9)).toISOString() : null,
			offerExpiresAt: isOffer ? new Date(Date.UTC(2026, 6, 22, 9)).toISOString() : null,
			confirmedAt: isConfirmed ? new Date(Date.UTC(2026, 6, 18, 9)).toISOString() : null,
			canSendOffer: statusId === ObserverApplicationStatus.PENDING,
			blockedReason: statusId === ObserverApplicationStatus.PENDING ? null : 'Application is not ready for an offer.',
			gender: index % 2 ? 'Female' : 'Male',
			dateOfBirth: '1993-04-12',
			profilePhoto: '',
			...qualifications,
			accountName: `${firstName} ${lastName}`,
			accountNumber: '0000000000',
			bankName: 'Example Bank',
			faceId: '',
			twitterHandle: '',
			instagramHandle: '',
			facebookHandle: '',
			latitude: 6.5244,
			longitude: 3.3792,
			kycReviewRequired: false,
			dateCreated: appliedAt,
			status: statusId,
			reviewedAt: null,
			reviewedBy: null,
			statusHistory: [{ id: 1, previousStatusId: null, statusId: 1, changedAt: appliedAt, changedBy: 'Applicant' }],
			banking: { accountName: `${firstName} ${lastName}`, accountNumber: '0000000000', bankName: 'Example Bank' },
			socialAccounts: {},
			preciseLocation: { latitude: 6.5244, longitude: 3.3792 },
		} satisfies IObserverApplicationDetail;
	});
	return { entities, applications };
};

const getStore = (scope: IObserverManagementScopeParams) => {
	const key = scopeKey(scope);
	if (!stores.has(key)) stores.set(key, buildStore(scope));
	return stores.get(key)!;
};

const notify = () => subscribers.forEach((subscriber) => subscriber());

export const subscribeObserverMock = (subscriber: () => void) => {
	subscribers.add(subscriber);
	return () => {
		subscribers.delete(subscriber);
	};
};

export const resetObserverMock = () => {
	stores.clear();
	notify();
};

/**
 * Strips only the four grouped shapes the DETAIL response adds. It used to also strip the
 * qualification answers, which are on the list DTO — so a mock list row scored 0 while the
 * server's own list carries the flags the score is computed from.
 */
const summarize = (application: IObserverApplicationDetail): IObserverApplicationSummary => {
	const {
		statusHistory: _statusHistory,
		banking: _banking,
		socialAccounts: _socialAccounts,
		preciseLocation: _preciseLocation,
		...summary
	} = application;
	return summary;
};

const paginate = <T>(items: T[], pageNumber = 1, pageSize = 20): IApiResponse<T[]> => ({
	...ok(items.slice((pageNumber - 1) * pageSize, pageNumber * pageSize)),
	currentPage: pageNumber,
	pageSize,
	totalCount: items.length,
	totalPages: Math.max(1, Math.ceil(items.length / pageSize)),
});

export const getMockObserverApplications = (params: IObserverApplicationListParams) => {
	const store = getStore(params);
	let applications = store.applications.filter(
		(application) =>
			(!params.divisionType || application.divisionType === params.divisionType) &&
			(!params.entityCode || application.entityCode === params.entityCode) &&
			(!params.statusId || application.statusId === params.statusId) &&
			(params.qualifications ?? []).every((key) => application[key]) &&
			(!params.ApplicationIds?.length || params.ApplicationIds.includes(application.id))
	);
	const search = params.search?.trim().toLowerCase();
	if (search) {
		applications = applications.filter((application) =>
			[fullName(application), application.email, application.phoneNumber, application.entityCode, application.entityName]
				.join(' ')
				.toLowerCase()
				.includes(search)
		);
	}
	const direction = params.sortOrder === 'desc' ? -1 : 1;
	applications = [...applications].sort((a, b) => {
		if (params.sortBy === 'score') return (a.qualificationScore - b.qualificationScore) * direction;
		if (params.sortBy === 'name') return fullName(a).localeCompare(fullName(b)) * direction;
		return a.appliedAt.localeCompare(b.appliedAt) * direction;
	});
	return paginate(applications.map(summarize), params.PageNumber, params.PageSize);
};

export const getMockObserverApplicationDetail = (scope: IObserverManagementScopeParams, id: number) => {
	const application = getStore(scope).applications.find((item) => item.id === id);
	if (!application) throw new Error('Observer application not found');
	return ok(application);
};

const changeStatus = (
	application: IObserverApplicationDetail,
	statusId: IObserverApplicationDetail['statusId'],
	reason?: string
) => {
	const previousStatusId = application.statusId;
	application.statusId = statusId;
	application.canSendOffer = statusId === ObserverApplicationStatus.PENDING;
	application.blockedReason = application.canSendOffer ? null : 'Application is not ready for an offer.';
	application.statusHistory.push({
		id: application.statusHistory.length + 1,
		previousStatusId,
		statusId,
		changedAt: new Date().toISOString(),
		changedBy: 'Mock Observer Admin',
		reason,
	});
	return previousStatusId;
};

const findApplication = (scope: IObserverManagementScopeParams, id: number) => {
	const store = getStore(scope);
	const application = store.applications.find((item) => item.id === id);
	if (!application) throw new Error('Observer application not found');
	return { store, application };
};

export const mockRejectObserverApplication = (scope: IObserverManagementScopeParams, id: number, reason: string) => {
	const { application } = findApplication(scope, id);
	changeStatus(application, ObserverApplicationStatus.REJECTED, reason);
	notify();
	return ok({});
};

const csvCell = (value: string | number) => `"${String(value).replaceAll('"', '""')}"`;

export const mockExportObserverApplications = (params: IObserverApplicationListParams) => {
	const response = getMockObserverApplications({ ...params, PageNumber: 1, PageSize: Number.MAX_SAFE_INTEGER });
	const rows = response.data.map((application) =>
		[
			application.id,
			fullName(application),
			application.email,
			application.phoneNumber,
			application.entityCode,
			application.entityName,
			application.qualificationScore,
			application.statusId,
			application.appliedAt,
		]
			.map(csvCell)
			.join(',')
	);
	return new Blob(
		[
			[
				['ID', 'Name', 'Email', 'Phone', 'Entity Code', 'Entity', 'Score', 'Status ID', 'Applied At'].map(csvCell).join(','),
				...rows,
			].join('\n'),
		],
		{ type: 'text/csv;charset=utf-8' }
	);
};
